import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  MapPin,
  Plus,
  Trash2,
  Edit,
  Building,
  Phone,
  Mail,
  User,
  Loader2,
  Grid3x3,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VenueManager = () => {
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    num_courts: 2,
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const res = await fetch(`${API_URL}/api/venues`);
      if (res.ok) {
        setVenues(await res.json());
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      toast.error('Please login to manage venues');
      return;
    }

    setSaving(true);
    try {
      const url = editingVenue 
        ? `${API_URL}/api/venues/${editingVenue.id}`
        : `${API_URL}/api/venues`;
      
      const res = await fetch(url, {
        method: editingVenue ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const venue = await res.json();
        if (editingVenue) {
          setVenues(venues.map(v => v.id === venue.id ? venue : v));
          toast.success('Venue updated');
        } else {
          setVenues([...venues, venue]);
          toast.success('Venue created');
        }
        setShowDialog(false);
        resetForm();
      } else {
        toast.error('Failed to save venue');
      }
    } catch (error) {
      toast.error('Error saving venue');
    }
    setSaving(false);
  };

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    setFormData({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      zip_code: venue.zip_code || '',
      num_courts: venue.courts?.length || 2,
      contact_name: venue.contact_name || '',
      contact_phone: venue.contact_phone || '',
      contact_email: venue.contact_email || '',
      notes: venue.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (venueId) => {
    if (!window.confirm('Delete this venue?')) return;

    try {
      const res = await fetch(`${API_URL}/api/venues/${venueId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setVenues(venues.filter(v => v.id !== venueId));
        toast.success('Venue deleted');
      }
    } catch (error) {
      toast.error('Error deleting venue');
    }
  };

  const resetForm = () => {
    setEditingVenue(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      num_courts: 2,
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      notes: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-rooam-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rooam-black">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 md:mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">Venue Manager</h1>
            <p className="text-sm sm:text-base text-gray-400">Manage locations and courts</p>
          </div>
          
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold">
                <Plus className="h-4 w-4 mr-2" />
                Add Venue
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingVenue ? 'Edit Venue' : 'Add New Venue'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Venue Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Sports Arena"
                    className="bg-rooam-black border-rooam-gray-light text-white"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    className="bg-rooam-black border-rooam-gray-light text-white"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-300">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Atlanta"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="GA"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">ZIP</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      placeholder="30301"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Number of Courts</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.num_courts}
                    onChange={(e) => setFormData({ ...formData, num_courts: parseInt(e.target.value) || 1 })}
                    className="bg-rooam-black border-rooam-gray-light text-white"
                  />
                </div>

                <div className="border-t border-rooam-gray-light pt-4">
                  <p className="text-sm text-gray-400 mb-3">Contact Information</p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-300">Contact Name</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        placeholder="John Smith"
                        className="bg-rooam-black border-rooam-gray-light text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-300">Phone</Label>
                        <Input
                          value={formData.contact_phone}
                          onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="bg-rooam-black border-rooam-gray-light text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Email</Label>
                        <Input
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                          placeholder="contact@venue.com"
                          className="bg-rooam-black border-rooam-gray-light text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Parking info, access instructions..."
                    className="bg-rooam-black border-rooam-gray-light text-white"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingVenue ? 'Update Venue' : 'Create Venue'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {venues.length === 0 ? (
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-12 text-center">
              <Building className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Venues Yet</h3>
              <p className="text-gray-400 mb-4">Add your first venue to start scheduling games</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <Card key={venue.id} className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-white text-lg">{venue.name}</CardTitle>
                      {venue.city && venue.state && (
                        <p className="text-sm text-gray-400 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {venue.city}, {venue.state}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white"
                        onClick={() => handleEdit(venue)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:bg-red-500/10"
                        onClick={() => handleDelete(venue.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {venue.address && (
                    <p className="text-sm text-gray-400 mb-3">{venue.address}</p>
                  )}
                  
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Courts</p>
                    <div className="flex flex-wrap gap-2">
                      {venue.courts?.map((court) => (
                        <Badge 
                          key={court.id} 
                          className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue"
                        >
                          <Grid3x3 className="h-3 w-3 mr-1" />
                          {court.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {(venue.contact_name || venue.contact_phone || venue.contact_email) && (
                    <div className="border-t border-rooam-gray-light pt-3 space-y-1">
                      {venue.contact_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <User className="h-3 w-3" /> {venue.contact_name}
                        </p>
                      )}
                      {venue.contact_phone && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {venue.contact_phone}
                        </p>
                      )}
                      {venue.contact_email && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {venue.contact_email}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueManager;
